import * as React from 'react';
import { cn } from '../../lib/cn';

/**
 * Input — shadcn text-field idiom, themed onto HofSync brand tokens.
 *
 * Preflight is deliberately excluded from the DS Tailwind build (see CLAUDE.md),
 * so a native <input> keeps its UA appearance, border and focus outline. We
 * therefore neutralise that chrome per-component — exactly as `button.tsx` drops
 * its UA border with `tw:border-0`:
 *   - `tw:appearance-none` removes UA control styling,
 *   - an explicit `tw:border tw:border-input` + `tw:bg-card` re-establish the field
 *     surface on the brand `--input` / `--card` tokens,
 *   - `tw:focus-visible:outline-none` + `tw:focus-visible:ring-2 tw:ring-ring` swap
 *     the UA focus outline for the brand ring (`--ring`),
 *   - `tw:font-sans` resets the UA font-family — form controls do NOT inherit
 *     the document font, so without it the <input> keeps the UA control font (#1927).
 * Without this the input would render unstyled in the app (which has no preflight),
 * even though it may look fine in a preflight-bearing context.
 */
export type InputProps = React.InputHTMLAttributes<HTMLInputElement>;

export const Input = React.forwardRef<HTMLInputElement, InputProps>(
  ({ className, type = 'text', ...props }, ref) => (
    <input
      ref={ref}
      type={type}
      className={cn(
        'tw:flex tw:h-9 tw:w-full tw:appearance-none tw:rounded-md tw:border tw:border-input tw:bg-card tw:px-3 tw:py-1 tw:font-sans tw:text-sm tw:text-foreground tw:shadow-sm tw:transition-colors',
        'tw:placeholder:text-muted-foreground',
        'tw:focus-visible:outline-none tw:focus-visible:ring-2 tw:focus-visible:ring-ring',
        'tw:disabled:cursor-not-allowed tw:disabled:opacity-50',
        className
      )}
      {...props}
    />
  )
);
Input.displayName = 'Input';
